import { getUuid } from '@sibe/utilities/common';
import type { HsGeometry } from '../../graphic-engine-new/geometry/geometry.interface';
import { HsDefaultMaterial } from '../../graphic-render-new/materials/default-material.class';
import { HsGraphicObject } from '../../graphic-render-new/objects/graphic-object.class';
import { HsShaderData } from '../../graphic-render-new/shaders/classes/shader-data.class';
import type { HsShader } from '../../graphic-render-new/shaders/classes/shader.class';
import { HsShaderObjectType } from '../../graphic-render-new/shaders/enums/shader-object-type.enum';
import type { HsColor } from '../../math-new/objects/color.class';
import { HsIndexedGeometryData } from '../geometry-utils/indexed-geometry-data.class';
import type { HsGraphics } from '../../graphic-render-new/graphics.class';
import type { HsGeometryData } from '../geometry-utils/geometry-data.class';
import { hsIndexGeometry } from '../geometry-utils/index-geometry.function';
import { hsMakeIndexedGeometry } from '../geometry-utils/make-indexed-geometry.function';
import type { HsMaterial } from '../../graphic-render-new/materials/material.class';
import type { HsHypersceneTypes } from '../../math-new/types/hyperscene-types.type';
import { HsQuaternion } from '../../math-new/objects/quaternion.class';
import { HsMatrix4 } from '../../math-new/objects/matrix4.class';
import { hsGenerateSphereGeometryData } from './generate-sphere-function';
import { hsGenerateCubeGeometryData } from './generate-cube-geometry-data.function';
import { hsGenerateCylinderGeometryData } from './generate-cylinder-geometry-data.function';
import { hsGenerateConeGeometryData } from './generate-cone-geometry-data.function';
import { hsGeneratePlaneGeometryData } from './generate-plane-geometry-data.function';
import { hsGenerateTorusGeometryData } from './generate-torus-geometry-data.function';
import { hsGenerateCapsuleGeometryData } from './generate-capsule-geometry-data.function';
import type { HsGraphicQueue } from '../../graphic-render-new/objects/graphic-queue.class';
import { hsGenerateAngleGeometryData } from './generate-angle-geometry-data.function';
import { hsGenerateCornerGeometryData } from './generate-corner-geometry-data.function';
import { hsGenerateIndexedPlaneGeometryData } from './generate-indexed-plane-geometry-data.function';
import { hsMakeTexturedIndexedGeometry } from '../geometry-utils/make-textured-indexed-geometry.function';
import { hsGenerateArrowGeometryData } from './generate-arrow-geometry-data.function';

/**
 * Handles s primitives.
 */
export class HsPrimitives {
  private material: HsMaterial | null = null;
  private queue: HsGraphicQueue | null = null;
  private readonly graphics: HsGraphics;

  constructor(graphics: HsGraphics) {
    this.graphics = graphics;
  }

  public makeSphere(
    position: HsHypersceneTypes.Array3,
    size: HsHypersceneTypes.Array3 = [1, 1, 1],
    quality: number = 12
  ): HsGraphicObject | null {
    return this.makeObject(hsGenerateSphereGeometryData(quality), position, undefined, size);
  }

  public makeCube(
    position: HsHypersceneTypes.Array3,
    rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
    size: HsHypersceneTypes.Array3 = [1, 1, 1]
  ): HsGraphicObject | null {
    return this.makeObject(hsGenerateCubeGeometryData(), position, rotation, size);
  }

  public makeCylinder(
    position: HsHypersceneTypes.Array3,
    rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
    size: HsHypersceneTypes.Array3 = [1, 1, 1],
    quality: number = 12
  ): HsGraphicObject | null {
    return this.makeObject(hsGenerateCylinderGeometryData(quality), position, rotation, size);
  }

  public makeCone(
    position: HsHypersceneTypes.Array3,
    rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
    size: HsHypersceneTypes.Array3 = [1, 1, 1],
    quality: number = 12
  ): HsGraphicObject | null {
    return this.makeObject(hsGenerateConeGeometryData(quality), position, rotation, size);
  }

  public makePlane(
    position: HsHypersceneTypes.Array3,
    rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
    size: HsHypersceneTypes.Array3 = [1, 1, 1]
  ): HsGraphicObject | null {
    return this.makeObject(hsGeneratePlaneGeometryData(), position, rotation, size);
  }

  public makePlaneWithUvs(
    position: HsHypersceneTypes.Array3,
    rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
    size: HsHypersceneTypes.Array3 = [1, 1, 1]
  ): HsGraphicObject | null {
    return this.makeObject(hsGenerateIndexedPlaneGeometryData(), position, rotation, size);
  }

  public makeTorus(
    position: HsHypersceneTypes.Array3,
    rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
    size: HsHypersceneTypes.Array3 = [1, 1, 1],
    radius: number,
    innerRadius: number,
    segments: number = 8,
    quality: number = 32
  ): HsGraphicObject | null {
    return this.makeObject(
      hsGenerateTorusGeometryData(radius, innerRadius, segments, quality),
      position,
      rotation,
      size
    );
  }

  public makeAngle(
    position: HsHypersceneTypes.Array3,
    rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
    size: HsHypersceneTypes.Array3 = [1, 1, 1],
    width: number = 1
  ): HsGraphicObject | null {
    return this.makeObject(hsGenerateAngleGeometryData(width), position, rotation, size);
  }

  public makeCorner(
    position: HsHypersceneTypes.Array3,
    rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
    size: HsHypersceneTypes.Array3 = [1, 1, 1],
    width: number = 1
  ): HsGraphicObject | null {
    return this.makeObject(hsGenerateCornerGeometryData(width), position, rotation, size);
  }

  public setQueue(queue: HsGraphicQueue): void {
    this.queue = queue;
  }

  public makeCapsule(
    position: HsHypersceneTypes.Array3,
    rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
    size: HsHypersceneTypes.Array3 = [1, 1, 1],
    quality: number = 12
  ): HsGraphicObject | null {
    return this.makeObject(hsGenerateCapsuleGeometryData(quality), position, rotation, size);
  }

  public makeArrow(
    position: HsHypersceneTypes.Array3,
    rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
    size: HsHypersceneTypes.Array3 = [1, 1, 1],
    quality: number = 12,
    handWidth: number = 0.5,
    pointerWidth: number = 1
  ): HsGraphicObject | null {
    return this.makeObject(hsGenerateArrowGeometryData(quality, handWidth, pointerWidth), position, rotation, size);
  }

  public setMaterial(material: HsMaterial): void {
    this.material = material;
  }

  public setDefaultMaterial(gl: WebGL2RenderingContext, color: HsColor): void {
    const phongShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    phongShaderData.addColor().addDepth().addNormals().addRayIntersection().addDiffuseLighting().addSpecularLighting();
    const phongShader: HsShader = phongShaderData.makeShader();
    this.material = new HsDefaultMaterial(gl, phongShader);
    this.material.color.copy(color);
  }

  private makeObject(
    data: HsGeometryData,
    position: HsHypersceneTypes.Array3,
    rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
    size: HsHypersceneTypes.Array3 = [1, 1, 1]
  ): HsGraphicObject | null {
    if (this.material === null) {
      throw new Error('Primitives: missing material');
    }
    const indexedData: HsIndexedGeometryData = data instanceof HsIndexedGeometryData ? data : hsIndexGeometry(data);
    let objectGeometry: HsGeometry | null = null;
    if (indexedData.uvs === undefined) {
      objectGeometry = hsMakeIndexedGeometry(this.graphics, indexedData);
    } else {
      objectGeometry = hsMakeTexturedIndexedGeometry(this.graphics, indexedData);
    }
    if (objectGeometry === null) {
      return null;
    }
    const object: HsGraphicObject = new HsGraphicObject(getUuid(), 'object', objectGeometry, this.material);
    const matrix: HsMatrix4 = HsMatrix4.compose(position, rotation.values, size);
    object.worldMatrix.multiply(matrix);
    object.updateBoundingSphere();
    if (this.queue !== null) {
      this.queue.addObject(object);
    }
    return object;
  }
}
